import {ServiceBase} from "../service-base";
import {ITurnstileService} from "./turnstile.service.interface";
import {NullableString} from "../../types/nullable-types";
import {ZIndex} from "../../types/z-index";

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const GET_TOKEN_TIMEOUT_MILLISECONDS = 10 * 1000;

interface ITurnstileApi {
    render(container: HTMLElement, params: Record<string, unknown>): string | undefined;
    reset(widgetId: string): void;
    execute(widgetId: string): void;
}

export class TurnstileService extends ServiceBase implements ITurnstileService {

    private _widgetId: string | null = null;
    private _initPromise: Promise<boolean> | null = null;
    private _pendingTokenResolver: ((token: NullableString) => void) | null = null;

    get isEnabled(): boolean {
        if(this.services.device.isHybrid) {
            //the mobile app channel is covered by Firebase App Check
            return false;
        }

        return Boolean(this.services.configuration.envConfigs.turnstileSiteKey);
    }

    async getToken(): Promise<string | null> {
        try {
            if(!this.isEnabled) {
                return null;
            }

            if(!await this._init()) {
                return null;
            }

            return await this._executeChallenge();
        } catch (err) {
            this.services.logger.error('Turnstile getToken failed', err);
            return null;
        }
    }

    private get _turnstileApi(): ITurnstileApi | undefined {
        return (window as any).turnstile as ITurnstileApi | undefined;
    }

    private _init(): Promise<boolean> {
        if(!this._initPromise) {
            this._initPromise = this._doInit();
        }

        return this._initPromise;
    }

    private async _doInit(): Promise<boolean> {
        try {
            await this.services.scriptLoader.loadScript({url: TURNSTILE_SCRIPT_URL});

            const turnstileApi = this._turnstileApi;
            if(!turnstileApi) {
                this.services.logger.error('Turnstile script loaded but the API is not available');
                return false;
            }

            const widgetId = turnstileApi.render(this._createWidgetContainer(), {
                sitekey: this.services.configuration.envConfigs.turnstileSiteKey,
                execution: 'execute',
                appearance: 'interaction-only',
                callback: (token: string) => this._resolvePendingToken(token),
                'error-callback': () => {
                    this._resolvePendingToken(null);
                    return true; //don't let the widget also log to the console
                },
                'timeout-callback': () => this._resolvePendingToken(null)
            });

            if(!widgetId) {
                this.services.logger.error('Turnstile widget failed to render');
                return false;
            }

            this._widgetId = widgetId;
            return true;
        } catch (err) {
            this.services.logger.error('Turnstile initialization failed', err);
            return false;
        }
    }

    private _createWidgetContainer(): HTMLElement {
        //invisible while no interaction is needed ('interaction-only'), but positioned so
        //the checkbox can actually be displayed when Cloudflare requires a visible challenge
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.bottom = '16px';
        container.style.left = '50%';
        container.style.transform = 'translateX(-50%)';
        container.style.zIndex = ZIndex.CloudflareTurnstile.toString();
        document.body.appendChild(container);
        return container;
    }

    private _resolvePendingToken(token: NullableString): void {
        const resolve = this._pendingTokenResolver;
        this._pendingTokenResolver = null;
        resolve?.(token);
    }

    private async _executeChallenge(): Promise<NullableString> {
        const turnstileApi = this._turnstileApi;
        if(!turnstileApi || !this._widgetId) {
            return null;
        }

        const tokenPromise = new Promise<NullableString>(resolve => {
            this._pendingTokenResolver = resolve;
        });

        //tokens are single use with a 300 seconds TTL so a fresh one is requested on every call
        turnstileApi.reset(this._widgetId);
        turnstileApi.execute(this._widgetId);

        const timeoutPromise = new Promise<NullableString>(resolve => {
            window.setTimeout(() => resolve(null), GET_TOKEN_TIMEOUT_MILLISECONDS);
        });

        return await Promise.race([tokenPromise, timeoutPromise]);
    }
}
